import React, { useState, useEffect, useRef } from 'react';
import FileUploader from '../FileUploader';
import { FileTypeDetails, FileTableEntry, FileMetadataType } from '../../../store/Models/Documents/DocumentModels';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import {ToxnotServiceClient} from "../../../store/ToxnotServiceClient";
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../ToxnotReactModal';

interface CmrtFileErrorModel{
    ErrorType: string;
    Sheet: string;
    Cell: string
}

interface ImportCMRTProps {
    localizer: IStringLocalizer;
    inventoryId?: string;
    questionIndex?: number;
    dataSource?: FileTableEntry[];
    onFileUpload: (fileDetails: FileTableEntry, file: File, typeId: number, index?: number) => Promise<FileTableEntry>;
    onMetadataChange: (file: FileTableEntry) => void;
    onFileDelete: (fileId: string) => Promise<void>;
    onResponseChange: (value: any, valueComment?: string, files?: FileTableEntry[], fileIds?: string[], index?: number) => void;
}

const ImportCMRT: React.FC<ImportCMRTProps> = ({
    localizer,
    inventoryId,
    questionIndex,
    dataSource = [],    
    onFileUpload,
    onMetadataChange,
    onFileDelete,
    onResponseChange
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [errorList, setErrorList] = useState<CmrtFileErrorModel[] | null>(null);
    const [isFileUploaded, setIsFileUploaded] = useState(dataSource.length > 0);
    const serviceClient = new ToxnotServiceClient();
    const isDeletingRef = useRef(false);
    
    // Update isFileUploaded state when dataSource changes
    // Only update if we're not in the middle of a delete operation
    useEffect(() => {
        if (!isDeletingRef.current) {
            setIsFileUploaded(dataSource.length > 0);
        }
    }, [dataSource]);

    // Reset the deleting flag when dataSource actually changes after deletion
    useEffect(() => {
        if (isDeletingRef.current && dataSource.length === 0) {
            isDeletingRef.current = false;
        }
    }, [dataSource.length]);

    const fileTypes: FileTypeDetails[] = [
        {
            displayValue: localizer.localizeString("Add Document"),
            value: FileMetadataType.PlainDocument
        }
    ];

    // Custom file upload handler that calls the fetchCmrtV2 API after upload
    const handleFileUpload = async (fileDetails: FileTableEntry, file: File, typeId: number, index?: number): Promise<FileTableEntry> => {
        setIsLoading(true);
        setErrorMessage(null);

        try {
            // Check if it's an Excel file
            const isExcelFile = file && (
                file.type === "application/vnd.ms-excel" ||  // .xls
                file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || // .xlsx
                /\.(xls|xlsx)$/i.test(file.name) // Fallback: Check file extension
            );

            if (!isExcelFile) {
                setIsLoading(false);
                let localizedMessage = localizer.localizeString('The uploaded file format is not supported. Please upload a file in one of the following formats: .xls, .xlsx');
                setErrorMessage(localizedMessage);
                throw new Error('The uploaded file format is not supported. Please upload a file in one of the following formats: .xls, .xlsx');
            }

            // Upload the file using the provided function
            const uploadedFile = await onFileUpload(fileDetails, file, typeId, index);
            // Handle null response
            if (uploadedFile === null || uploadedFile === undefined) {
                setIsLoading(false);
                setErrorMessage(localizer.localizeString("The uploaded file does not match the standard CMRT format. Please ensure your file follows the CMRT structure and try again. If the issue persists, refer to our Help Center or contact support@3eco.com for assistance."));
                throw new Error('The uploaded file does not match the standard CMRT format. Please ensure your file follows the CMRT structure and try again. If the issue persists, refer to our Help Center or contact support@3eco.com for assistance.');
            }
            
            await processCmrtFile(file, uploadedFile.fileId);
            setIsLoading(false);
            // Successfully uploaded, disable button
            setIsFileUploaded(true);
            return uploadedFile;
        } catch (error) {
            const errorMessage = error instanceof Error
                ? error.message
                : localizer.localizeString('The uploaded file does not match the standard CMRT format. Please ensure your file follows the CMRT structure and try again. If the issue persists, refer to our Help Center or contact support@3eco.com for assistance.');

            setIsLoading(false);
            setErrorMessage(errorMessage);
            throw error;
        }
    };

    // Process the CMRT file using the existing API
    const processCmrtFile = async (file: File, fileId: any): Promise<void> => {
        try {
            // Use await pattern as shown in your code example
            const fetchCmrt: any = await serviceClient.fetchCmrtV2(file) as any;

            if(fetchCmrt === null){
                setErrorMessage(localizer.localizeString("The uploaded file does not match the standard CMRT format. Please ensure your file follows the CMRT structure and try again. If the issue persists, refer to our Help Center or contact support@3eco.com for assistance."));
                await handleFileDelete(fileId);
                return;
            }

            if(fetchCmrt.errors){
                var errorList: Array<CmrtFileErrorModel> = JSON.parse(fetchCmrt.errors);
                
                // Filter out duplicate errors for the same cell, prioritizing EmptyRequiredCell over WrongValue
                const filteredErrors = errorList.reduce((acc: CmrtFileErrorModel[], current) => {
                    const existingError = acc.find(e => e.Sheet === current.Sheet && e.Cell === current.Cell);
                    
                    if (!existingError) {
                        // No error for this cell yet, add it
                        acc.push(current);
                    } else if (current.ErrorType === 'EmptyRequiredCell' && existingError.ErrorType === 'WrongValue') {
                        // Replace WrongValue with EmptyRequiredCell for the same cell
                        const index = acc.indexOf(existingError);
                        acc[index] = current;
                    }
                    // If existingError is EmptyRequiredCell, don't add WrongValue
                    
                    return acc;
                }, []);
                
                setErrorList(filteredErrors);
                // Show the modal using jQuery
                if (typeof $ !== 'undefined') {
                    $('#cmrt-import-error-modal').modal('show');
                }
                await handleFileDelete(fileId);
                return;
            }

            // Handle null response
            if (Object.keys(fetchCmrt.cmrtJson || {}).length === 0 || fetchCmrt.cmrtJson === undefined) {
                setErrorMessage(localizer.localizeString("The uploaded file does not match the standard CMRT format. Please ensure your file follows the CMRT structure and try again. If the issue persists, refer to our Help Center or contact support@3eco.com for assistance."));
                await handleFileDelete(fileId);
                return;
            }

            // Process successful response
            const cmrtJson = fetchCmrt.cmrtJson;
            
            // Call onResponseChange with the mapped string and the current files
            const responseString = mapCmrtJsonToString(cmrtJson, fileId);
            
            // Create a copy of dataSource to avoid modifying the original
            const updatedDataSource = [...dataSource];
            
            // Check if the newly uploaded file is already in the dataSource
            const fileExists = updatedDataSource.some(file => file.fileId === fileId);
            
            // If not, add it
            if (!fileExists) {
                updatedDataSource.push({
                    fileId: fileId,
                    filename: file.name,
                    metadataJson: JSON.stringify({ description: "" }),
                    inventoryId: inventoryId && inventoryId != '' ? inventoryId: null,
                    metadataType: 0
                } as FileTableEntry);
            }
            
            // Make sure we pass the updated dataSource (files) and fileIds to preserve them during navigation
            await onResponseChange(responseString, "", updatedDataSource);

            if (typeof $ !== 'undefined') {
                $('.documents-add-file-button + #file-input-element[data-summary-key-prefix="cmrt_files"]')
                    .prev('.documents-add-file-button')
                    .attr('disabled', 'disabled');
            }

        } catch (error) {
            await handleFileDelete(fileId);
            throw new Error('The uploaded file does not match the standard CMRT format. Please ensure your file follows the CMRT structure and try again. If the issue persists, refer to our Help Center or contact support@3eco.com for assistance.');
        }
    };


    // Mapping function to convert cmrtJson to the desired format
    const mapCmrtJsonToString = (cmrtJson: any, fileId: any) => {

        const mappedResult = {
            cmrt_response_key: "yes",

            cmrt_files_0: fileId.toString(),
            cmrt_files_count: "1",
            cmrt_company_name: cmrtJson.ConflictMineralV6XmlTemplate.BusinessInfo?.Response?.SupplyCompany || "",
            cmrt_declaration_of_scope_or_class: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.DeclarationScope === "Custom"
                ? "User Defined"
                : (cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.DeclarationScope || ""),
            cmrt_declaration_of_scope: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.DescriptionOfScope || "",
            cmrt_company_unique_id: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyInfo?.CompanyUniqueId || "",
            cmrt_company_unique_id_authority: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyInfo?.CompanyUniqueIdAuthority || "",
            cmrt_company_address: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyInfo?.CompanyAddress || "",
            cmrt_company_contact_name: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyInfo?.ContactName || "",
            cmrt_company_contact_email: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyInfo?.ContactEmail || "",
            cmrt_company_authorizer: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyInfo?.AuthorizerName || "",
            cmrt_company_title: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyInfo?.AuthorizerTitle || "",
            cmrt_company_email: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyInfo?.AuthorizerEmail || "",
            cmrt_company_effective_date: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyInfo?.EffectiveDate?.split('T')[0] || "",

            cmrt_smelters_list: JSON.stringify(
                ((cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.SmelterList?.Smelter || []) as any[])
                    .map((smelter: any) => ({
                        ...smelter,
                        DoesAllFeedstockComeFromScrapOrRecycled: mapFeedstockValue(smelter.DoesAllFeedstockComeFromScrapOrRecycled)
                    }))
            ),


            cmrt_product_list: JSON.stringify(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.ProductList?.Product || []),

            cmrt_3tg_added_or_used: JSON.stringify(mapYesNoCommentFields(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.IntentionallyAddedOrUsed)),
            cmrt_3tg_remains: JSON.stringify(mapYesNoCommentFields(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.RemainedInProduct)),
            cmrt_3tg_from_covered_countries: JSON.stringify(mapYesNoCommentFields(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.DrcOrigin)),
            cmrt_3tg_all_recycled: JSON.stringify(mapYesNoCommentFields(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.ScrapOrigin)),
            cmrt_suppliers_response_rate: JSON.stringify(mapAnswersForPercentageFields(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.PercentageSuppliersProvidedResponse)),
            cmrt_all_smelters_identified: JSON.stringify(mapYesNoCommentFields(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.KnownSmelters)),
            cmrt_conflict_affected_high_risk_areas: JSON.stringify(mapYesNoCommentFields(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.Smelter3TGConflictAndHighRiskArea)),
            cmrt_smelter_info_received: JSON.stringify(mapYesNoCommentFields(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CfsValid)),

            cmrt_company_contact_phone: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyInfo?.ContactPhone?.Original || "",
            cmrt_company_phone: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyInfo?.AuthorizerPhone?.Original || "",
            cmrt_drc_conflict_free_policy: JSON.stringify({
                Answer: mapAnswerForDropDown(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.DrcConflictFreePolicy) || "",//cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.DrcConflictFreePolicy || "",
                Comment: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.DrcConflictFreePolicyComment || ""
            }),

            cmrt_is_drc_conflict_free_policy_public: JSON.stringify({
                Answer: mapAnswerForDropDown(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.IsDrcConflictFreePolicyPublic) || "",
                Comment: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.IsDrcConflictFreePolicyPublicComment || ""
            }),

            cmrt_suppliers_must_use_cfs_smelters: JSON.stringify({
                Answer: mapAnswerForDropDown(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.SuppliersMustUseCfsSmelters) || "",
                Comment: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.SuppliersMustUseCfsSmeltersComment || ""
            }),

            cmrt_due_diligence_for_cfs: JSON.stringify({
                Answer: mapAnswerForDropDown(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.DueDiligenceForCfs) || "",
                Comment: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.DueDiligenceForCfsComment || ""
            }),

            cmrt_conduct_conflict_minerals_survey: JSON.stringify({
                Answer: mapAnswerForDropDown(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.ConductConflictMineralsSurvey) || "",
                Comment: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.ConductConflictMineralsSurveyComment || ""
            }),

            cmrt_verify_suppliers_due_diligence: JSON.stringify({
                Answer: mapAnswerForDropDown(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.VerifySuppliersDueDiligence) || "",
                Comment: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.VerifySuppliersDueDiligenceComment || ""
            }),

            cmrt_verification_includes_corrective_actions: JSON.stringify({
                Answer: mapAnswerForDropDown(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.VerificationIncludesCorrectiveActions) || "",
                Comment: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.VerificationIncludesCorrectiveActionsComment || ""
            }),

            cmrt_subject_to_sec_cm_disclosure_rule: JSON.stringify({
                Answer: mapAnswerForDropDown(cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.SubjectToSecCmDisclosureRule) || "",
                Comment: cmrtJson.ConflictMineralV6XmlTemplate.ConflictMinerals?.CompanyWideQuestions?.SubjectToSecCmDisclosureRuleComment || ""
            }),

            cmrt_response_date: new Date().toISOString().split('T')[0]
        };

        return JSON.stringify(mappedResult);
    };

    const mapFeedstockValue = (value: any): string => {
        if (typeof value === 'object' && value?.['@xsi:nil'] === 'true') {
            return "Unknown";
        }
        return typeof value === 'string' ? value : "Unknown";
    };

    const cmrtImportErrorMessagesMap: Readonly<{ [type: string]: string }> = {
        "EmptyRequiredCell": "Empty Required Cell",
        "WrongValue": "Wrong Value"
    }


    // Wrapped delete handler
    const handleFileDelete = async (fileId: string): Promise<void> => {
        isDeletingRef.current = true; // Prevent useEffect from overriding our state
        
        try {
            // Perform the deletion silently in the background
            await onFileDelete(fileId);
            
            //setIsLoading(false);
            setIsFileUploaded(false); // Always enable button after deletion
            
            // Remove the disabled attribute that might have been set by jQuery
            if (typeof $ !== 'undefined') {
                $('.documents-add-file-button + #file-input-element[data-summary-key-prefix="cmrt_files"]')
                    .prev('.documents-add-file-button')
                    .removeAttr('disabled');
            }
        } catch (error) {
            isDeletingRef.current = false; // Reset the flag on error
            throw new Error('Error deleting file.');
        }
    };
    
    const mapAnswerForDropDown = (answer: string) => {
        if(!answer){
            return "";
        }
        if(answer === "YesConformanceWithIpc1755"){
            return "Yes, in conformance with IPC1755 (e.g., CMRT)";
        }
        else if(answer === "YesOtherFormat"){
            return "Yes, using other format (describe)";
        }
        else if(answer === "YesWithTheSEC"){
            return "Yes, with the SEC";
        }
        else if(answer === "YesWithTheEU"){
            return "Yes, with the EU";
        }
        else if(answer === "YesWithTheSECAndTheEU"){
            return "Yes, with the SEC and the EU";
        }
        return answer;
    };

// Helper function to map fields with Answer and Comment
    const mapYesNoCommentFields = (field: any) => ({
        Ta: { Answer: field?.Ta, Comment: field?.TaComment },
        Sn: { Answer: field?.Sn, Comment: field?.SnComment },
        Au: { Answer: field?.Au, Comment: field?.AuComment },
        W: { Answer: field?.W, Comment: field?.WComment },
    });

    const mapAnswersForPercentageFields = (field: any) => ({
        Ta: {
            Answer: field?.Ta?.toLowerCase() === 'none' ? 'None' : field?.Ta,
            Comment: field?.TaComment
        },
        Sn: {
            Answer: field?.Sn?.toLowerCase() === 'none' ? 'None' : field?.Sn,
            Comment: field?.SnComment
        },
        Au: {
            Answer: field?.Au?.toLowerCase() === 'none' ? 'None' : field?.Au,
            Comment: field?.AuComment
        },
        W: {
            Answer: field?.W?.toLowerCase() === 'none' ? 'None' : field?.W,
            Comment: field?.WComment
        },
    });

    return (
        <div className="import-cmrt-container">
            {isLoading && (
                <div className="loading-message alert alert-info">
                    {localizer.localizeString("Processing CMRT file, please wait...")}
                </div>
            )}

            {errorMessage && (
                <div className="error-message alert alert-danger">
                    {localizer.localizeString(errorMessage)}
                </div>
            )}

            <FileUploader
                fileTypes={fileTypes}
                dataSource={dataSource}
                uploadFileAction={handleFileUpload}
                metadataChangeAction={onMetadataChange}
                deleteFileAction={handleFileDelete}
                addButtonLocalised={localizer.localizeString("Import CMRT")}
                localizer={localizer}
                inventoryId={inventoryId}
                questionIndex={questionIndex}
                shouldAddButtonDisabled={isFileUploaded} // Button disabled if file already uploaded
            />

            <ToxnotReactModal modalId="cmrt-import-error-modal" localizedTitle={localizer.localizeString("Error")}>
                <ToxnotReactModalBody>
                    <p>{localizer.localizeString("Unable to import CMRT data from the attached file. Please review and correct your data file.")}</p>
                    {errorList && errorList.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-striped table-bordered">
                                <thead className="thead-dark">
                                    <tr>
                                        <th scope="col">#</th>
                                        <th scope="col">{localizer.localizeString("Sheet")}</th>
                                        <th scope="col">{localizer.localizeString("Cell")}</th>
                                        <th scope="col">{localizer.localizeString("Error Type")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {errorList.map((err, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td><strong>{err.Sheet || 'N/A'}</strong></td>
                                            <td><code>{err.Cell || 'N/A'}</code></td>
                                            <td>{localizer.localizeString(cmrtImportErrorMessagesMap[err.ErrorType]) || localizer.localizeString('Unknown error')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer} />
            </ToxnotReactModal>
        </div>
    );
};

export default ImportCMRT;
